import React, { useState, useRef, useCallback, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog, DialogContent, DialogActions,
  Button, Typography, IconButton, Box, Chip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import CropIcon from '@material-ui/icons/Crop';

const HANDLE_SIZE = 10;

const useStyles = makeStyles(() => ({
  paper: {
    maxWidth: 1000,
    width: '95vw',
    maxHeight: '90vh',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    borderBottom: '1px solid #e0e0e0',
    background: '#fafafa',
  },
  body: {
    padding: 0,
    display: 'flex',
    flexDirection: 'row',
    minHeight: 450,
    overflow: 'hidden',
  },
  legendSide: {
    flex: 1,
    overflow: 'auto',
    background: '#f5f5f5',
    padding: 8,
    position: 'relative',
    cursor: 'crosshair',
  },
  imageContainer: {
    position: 'relative',
    display: 'inline-block',
  },
  image: {
    display: 'block',
    maxWidth: '100%',
    userSelect: 'none',
  },
  itemSide: {
    width: 280,
    borderLeft: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
  },
  itemHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    textAlign: 'center',
  },
  itemName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#333',
    marginBottom: 4,
  },
  itemMeta: {
    fontSize: 11,
    color: '#888',
  },
  symbolPreview: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  symbolImg: {
    maxWidth: 120,
    maxHeight: 120,
    objectFit: 'contain',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    background: '#fff',
  },
  symbolPlaceholder: {
    color: '#ccc',
    fontSize: 13,
    textAlign: 'center',
  },
  itemNav: {
    padding: '8px 12px',
    borderTop: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressBar: {
    padding: '6px 16px',
    fontSize: 11,
    color: '#888',
    textAlign: 'center',
    borderTop: '1px solid #f0f0f0',
  },
  selectionBox: {
    position: 'absolute',
    border: '2px solid #1976d2',
    background: 'rgba(25, 118, 210, 0.08)',
    zIndex: 10,
    pointerEvents: 'none',
  },
  handle: {
    position: 'absolute',
    width: HANDLE_SIZE,
    height: HANDLE_SIZE,
    background: '#1976d2',
    border: '1px solid #fff',
    borderRadius: 2,
    zIndex: 11,
    pointerEvents: 'auto',
  },
}));

/**
 * SymbolWizard — walks through all items letting the user assign symbols from the legend.
 *
 * Props:
 *   open, onClose
 *   items         — array of plan items
 *   getLegendUrl  — (item) => string, returns legend crop URL per item
 *   onSaveSymbol  — (itemId, dataUrl) => void
 */
const SymbolWizard = ({ open, onClose, items = [], getLegendUrl, onSaveSymbol }) => {
  const classes = useStyles();
  const imageRef = useRef(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [dragMode, setDragMode] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [dragRectStart, setDragRectStart] = useState(null);
  const [croppedDataUrl, setCroppedDataUrl] = useState(null);

  const currentItem = items[currentIdx] || null;
  const assignedCount = items.filter(i => i.symbolDataUrl).length;

  useEffect(() => {
    if (open) {
      setCurrentIdx(0);
      setRect(null);
      setCroppedDataUrl(null);
      setDragMode(null);
    }
  }, [open]);

  // Reset crop when switching items
  useEffect(() => {
    setRect(null);
    setCroppedDataUrl(null);
    setDragMode(null);
  }, [currentIdx]);

  const getRelPos = useCallback((e) => {
    const img = imageRef.current;
    if (!img) return null;
    const r = img.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(e.clientX - r.left, img.clientWidth)),
      y: Math.max(0, Math.min(e.clientY - r.top, img.clientHeight)),
    };
  }, []);

  const doCrop = useCallback((r) => {
    const img = imageRef.current;
    if (!img || !r || r.w < 3 || r.h < 3) { setCroppedDataUrl(null); return; }
    const sx = img.naturalWidth / img.clientWidth;
    const sy = img.naturalHeight / img.clientHeight;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(r.w * sx);
    canvas.height = Math.round(r.h * sy);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, r.x * sx, r.y * sy, r.w * sx, r.h * sy, 0, 0, canvas.width, canvas.height);
    setCroppedDataUrl(canvas.toDataURL('image/png'));
  }, []);

  const startDragHandle = useCallback((mode, e) => {
    e.stopPropagation();
    const pos = getRelPos(e);
    if (!pos) return;
    setDragMode(mode);
    setDragStart(pos);
    setDragRectStart(rect ? { ...rect } : null);
  }, [getRelPos, rect]);

  const handleMouseDown = useCallback((e) => {
    const pos = getRelPos(e);
    if (!pos) return;
    if (rect && pos.x >= rect.x && pos.x <= rect.x + rect.w && pos.y >= rect.y && pos.y <= rect.y + rect.h) {
      setDragMode('move');
      setDragStart(pos);
      setDragRectStart({ ...rect });
      return;
    }
    setDragMode('create');
    setDragStart(pos);
    setDragRectStart(null);
    setRect({ x: pos.x, y: pos.y, w: 0, h: 0 });
    setCroppedDataUrl(null);
  }, [rect, getRelPos]);

  const handleMouseMove = useCallback((e) => {
    if (!dragMode || !dragStart) return;
    const pos = getRelPos(e);
    if (!pos) return;
    const dx = pos.x - dragStart.x;
    const dy = pos.y - dragStart.y;
    if (dragMode === 'create') {
      setRect({ x: Math.min(dragStart.x, pos.x), y: Math.min(dragStart.y, pos.y), w: Math.abs(pos.x - dragStart.x), h: Math.abs(pos.y - dragStart.y) });
    } else if (dragMode === 'move' && dragRectStart) {
      setRect({ ...dragRectStart, x: dragRectStart.x + dx, y: dragRectStart.y + dy });
    } else if (dragRectStart) {
      const r = { ...dragRectStart };
      if (dragMode.includes('w')) { r.x += dx; r.w -= dx; }
      if (dragMode.includes('e')) { r.w += dx; }
      if (dragMode.includes('n')) { r.y += dy; r.h -= dy; }
      if (dragMode.includes('s')) { r.h += dy; }
      if (r.w < 5) r.w = 5;
      if (r.h < 5) r.h = 5;
      setRect(r);
    }
  }, [dragMode, dragStart, dragRectStart, getRelPos]);

  const handleMouseUp = useCallback(() => {
    if (dragMode && rect && rect.w > 3 && rect.h > 3) doCrop(rect);
    setDragMode(null);
    setDragStart(null);
    setDragRectStart(null);
  }, [dragMode, rect, doCrop]);

  const saveAndNext = () => {
    if (currentItem && croppedDataUrl && onSaveSymbol) {
      onSaveSymbol(currentItem.itemId, croppedDataUrl);
    }
    if (currentIdx < items.length - 1) {
      setCurrentIdx(currentIdx + 1);
    }
  };

  const skipItem = () => {
    if (currentIdx < items.length - 1) setCurrentIdx(currentIdx + 1);
  };

  const hs = HANDLE_SIZE / 2;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} classes={{ paper: classes.paper }}>
      <div className={classes.header}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <CropIcon style={{ fontSize: 20, color: '#666' }} />
          <Typography style={{ fontSize: 15, fontWeight: 600 }}>
            Assign Symbols ({assignedCount}/{items.length})
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Chip
            size="small"
            label={`${currentIdx + 1} of ${items.length}`}
            style={{ fontSize: 11 }}
          />
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </div>

      <DialogContent className={classes.body}>
        {/* Left: Legend image with crop tool */}
        <div
          className={classes.legendSide}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => { if (dragMode) handleMouseUp(); }}
        >
          <div className={classes.imageContainer}>
            {currentItem && getLegendUrl && (
              <img
                ref={imageRef}
                src={getLegendUrl(currentItem)}
                alt="Legend"
                className={classes.image}
                crossOrigin="anonymous"
                draggable={false}
              />
            )}
            {rect && rect.w > 0 && rect.h > 0 && (
              <>
                <div className={classes.selectionBox} style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }} />
                {[
                  { key: 'nw', x: rect.x - hs, y: rect.y - hs, cursor: 'nw-resize' },
                  { key: 'ne', x: rect.x + rect.w - hs, y: rect.y - hs, cursor: 'ne-resize' },
                  { key: 'sw', x: rect.x - hs, y: rect.y + rect.h - hs, cursor: 'sw-resize' },
                  { key: 'se', x: rect.x + rect.w - hs, y: rect.y + rect.h - hs, cursor: 'se-resize' },
                ].map(h => (
                  <div key={h.key} className={classes.handle} style={{ left: h.x, top: h.y, cursor: h.cursor }} onMouseDown={(e) => startDragHandle(h.key, e)} />
                ))}
              </>
            )}
          </div>
        </div>

        {/* Right: Current item info + symbol preview */}
        <div className={classes.itemSide}>
          {currentItem && (
            <>
              <div className={classes.itemHeader}>
                <div className={classes.itemName}>{currentItem.description}</div>
                <div className={classes.itemMeta}>
                  {currentItem.section} · Qty: {currentItem.parsedQty} {currentItem.parsedUnit}
                  {currentItem.size ? ` · ${currentItem.size}` : ''}
                </div>
              </div>

              <div className={classes.symbolPreview}>
                {croppedDataUrl ? (
                  <img src={croppedDataUrl} alt="Symbol" className={classes.symbolImg} />
                ) : currentItem.symbolDataUrl ? (
                  <img src={currentItem.symbolDataUrl} alt="Saved symbol" className={classes.symbolImg} style={{ opacity: 0.5 }} />
                ) : (
                  <div className={classes.symbolPlaceholder}>
                    Draw a rectangle on the legend to select this item's symbol
                  </div>
                )}
              </div>

              <div className={classes.itemNav}>
                <IconButton
                  size="small"
                  disabled={currentIdx === 0}
                  onClick={() => setCurrentIdx(currentIdx - 1)}
                >
                  <NavigateBeforeIcon />
                </IconButton>

                <Box display="flex" style={{ gap: 4 }}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<SkipNextIcon style={{ fontSize: 14 }} />}
                    onClick={skipItem}
                    disabled={currentIdx >= items.length - 1}
                    style={{ textTransform: 'none', fontSize: 11, color: '#999' }}
                  >
                    Skip
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={croppedDataUrl ? <CheckCircleIcon style={{ fontSize: 14 }} /> : <NavigateNextIcon style={{ fontSize: 14 }} />}
                    onClick={saveAndNext}
                    disabled={!croppedDataUrl && currentIdx >= items.length - 1}
                    style={{ textTransform: 'none', fontSize: 11 }}
                  >
                    {croppedDataUrl ? 'Save & Next' : 'Next'}
                  </Button>
                </Box>

                <IconButton
                  size="small"
                  disabled={currentIdx >= items.length - 1}
                  onClick={() => setCurrentIdx(currentIdx + 1)}
                >
                  <NavigateNextIcon />
                </IconButton>
              </div>

              <div className={classes.progressBar}>
                {assignedCount} of {items.length} symbols assigned
              </div>
            </>
          )}
        </div>
      </DialogContent>

      <DialogActions style={{ padding: '8px 16px' }}>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SymbolWizard;
